// src/firebase/firebase.jsx
import { initializeApp, getApps } from "firebase/app";
import {
    getAuth,
    setPersistence,
    browserLocalPersistence,
    connectAuthEmulator,
} from "firebase/auth";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";

/**
 * ✅ Firebase init (Vite + React)
 * - PRODUCTION by default (uses real Firebase)
 * - Optional emulator mode when VITE_USE_FIREBASE_EMULATORS is truthy
 *
 * Exports: app, auth, db, storage, functions, IS_EMU
 *
 * To force production mode:
 *   VITE_USE_FIREBASE_EMULATORS=false  (or remove it)
 *
 * Optional emulator env vars:
 *   VITE_AUTH_EMULATOR_HOST=127.0.0.1:9099
 *   VITE_FIRESTORE_EMULATOR_HOST=127.0.0.1:8081
 *   VITE_STORAGE_EMULATOR_HOST=127.0.0.1:9199
 *   VITE_FUNCTIONS_EMULATOR_HOST=127.0.0.1:5001
 *   VITE_FUNCTIONS_REGION=us-central1
 */

// ✅ Detect emulator mode from Vite env (accepts 1/true/yes/on)
const EMU_RAW = String(import.meta.env.VITE_USE_FIREBASE_EMULATORS ?? "")
    .trim()
    .toLowerCase();
export const IS_EMU = ["1", "true", "yes", "on"].includes(EMU_RAW);

// ✅ Functions region
const FUNCTIONS_REGION =
    String(import.meta.env.VITE_FUNCTIONS_REGION || "us-central1").trim() ||
    "us-central1";

// ✅ Default storage bucket fallback (matches new Firebase bucket format)
const DEFAULT_STORAGE_BUCKET =
    "business-directory-dev-5376d.firebasestorage.app";

// ✅ Firebase config (from Vite env)
const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "",
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "",
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "",
    storageBucket:
        import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || DEFAULT_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "",
    appId: import.meta.env.VITE_FIREBASE_APP_ID || "",
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || "",
};

// ✅ Basic validation (warn only; don’t crash)
const missingKeys = Object.entries(firebaseConfig)
    .filter(([_, v]) => !v)
    .map(([k]) => k);

if (missingKeys.length) {
    console.warn(
        "[Firebase] Missing env keys in firebaseConfig:",
        missingKeys.join(", ")
    );
}

if (import.meta.env.DEV) {
    console.log("[Firebase] storageBucket:", firebaseConfig.storageBucket);
    console.log("[Firebase] IS_EMU:", IS_EMU, "| FUNCTIONS_REGION:", FUNCTIONS_REGION);
}

// ✅ Prevent duplicate init during Vite hot reload
export const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);

// ✅ Core services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app, FUNCTIONS_REGION);

// ✅ PRODUCTION: keep auth session stable across refresh
// (In emulator mode, we intentionally avoid persistence to reduce confusion.)
if (!IS_EMU) {
    setPersistence(auth, browserLocalPersistence).catch(() => { });
}

/* ─────────────────────────────────────────────────────────────
   Optional: emulator wiring (ONLY when IS_EMU === true)
   This does NOT affect production mode.
   ───────────────────────────────────────────────────────────── */

function parseHostPort(value, fallbackHost, fallbackPort) {
    const raw0 = String(value || "").trim();
    if (!raw0) return { host: fallbackHost, port: fallbackPort };

    const raw = raw0.replace(/^https?:\/\//i, "").trim();
    if (!raw.includes(":")) return { host: raw || fallbackHost, port: fallbackPort };

    const [hostPart, portPart] = raw.split(":");
    const host = (hostPart || fallbackHost).trim();
    const port = Number(String(portPart || "").trim());

    return { host: host || fallbackHost, port: Number.isFinite(port) ? port : fallbackPort };
}

function connectEmulatorsOnce() {
    if (typeof window === "undefined") return;
    if (!IS_EMU) {
        if (import.meta.env.DEV) console.log("[Firebase] Production mode enabled (real Firebase).");
        return;
    }

    // Global guard (survives hot reload)
    const g = globalThis;
    if (g.__BD_EMU_CONNECTED__) return;
    g.__BD_EMU_CONNECTED__ = true;

    const authHost = import.meta.env.VITE_AUTH_EMULATOR_HOST;
    const fsHost = import.meta.env.VITE_FIRESTORE_EMULATOR_HOST;
    const stHost = import.meta.env.VITE_STORAGE_EMULATOR_HOST;
    const fnHost = import.meta.env.VITE_FUNCTIONS_EMULATOR_HOST;

    const authHP = parseHostPort(authHost, "127.0.0.1", 9099);
    const fsHP = parseHostPort(fsHost, "127.0.0.1", 8081);
    const stHP = parseHostPort(stHost, "127.0.0.1", 9199);
    const fnHP = parseHostPort(fnHost, "127.0.0.1", 5001);

    const authUrl = `http://${authHP.host}:${authHP.port}`;

    try {
        connectAuthEmulator(auth, authUrl, { disableWarnings: true });
    } catch {
        // ignore double-connect during hot reload
    }

    try {
        connectFirestoreEmulator(db, fsHP.host, fsHP.port);
    } catch { }

    try {
        connectStorageEmulator(storage, stHP.host, stHP.port);
    } catch { }

    try {
        connectFunctionsEmulator(functions, fnHP.host, fnHP.port);
    } catch { }

    console.log("[Firebase] Emulator mode enabled");
    console.log("  Auth:", authUrl);
    console.log("  Firestore:", `${fsHP.host}:${fsHP.port}`);
    console.log("  Storage:", `${stHP.host}:${stHP.port}`);
    console.log("  Functions:", `${fnHP.host}:${fnHP.port} (${FUNCTIONS_REGION})`);
}

connectEmulatorsOnce();
